import React, { FC, useRef, useState } from "react";
import { useDrag, useDrop, XYCoord } from "react-dnd";
import { useDispatch, useSelector } from "react-redux";
import {
  startAddElement,
  startEditElement,
} from "../../actions/elementListAction";
import { startMoveRow } from "../../actions/rowListAction";
import { startEditSession } from "../../actions/sessionListAction";
import { Row } from "../../models/rowModel";
import { AppState } from "../../reducers/rootReducer";
import { SystemTypes } from "../../utils/constants";
import { ItemTypes } from "../../utils/itemTypes";
import RenderElement from "./RenderElement";
import { produce } from "immer";
import SectionElement from "./SectionElement";
import { getAllSections } from "../../selectors/sectionListSelector";
import { Section } from "../../models/sectionModel";
import { startDropSection } from "../../actions/sectionListAction";
import ActionListComponent from "../utilComponents/actionListComponent/actionListComponent";

export interface RowElementProps {
  row: Row;
  index: number;
  isPreview?: boolean;
  isTab?: boolean;
}

const RowElement: FC<RowElementProps> = ({ row, index, isPreview = false, isTab = false }) => {
  const dispatch = useDispatch();
  const ref = useRef<HTMLDivElement>(null);
  const sessionState = useSelector((state: AppState) => state.session);
  const sectionState = useSelector(getAllSections);
  const elementTemplateState = useSelector(
    (state: AppState) => state.elementTemplateList
  );

  const selectRowAction = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation();
    const newSession = produce(sessionState, (draft) => {
      draft.selectedRowId = row.id;
      draft.selectedType = SystemTypes.ROW;
    });
    dispatch(startEditSession(newSession));
  };

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.ROW_MOVE,
    item: { dragItem: row, index: index },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  });

  const [{ isOver, isOverCurrent }, drop] = useDrop({
    accept: [ItemTypes.ROW_MOVE],
    drop(item: any, monitor) {},
    hover(item: any, monitor) {
      const hoverType = monitor.getItemType();
      if (hoverType === ItemTypes.ROW_MOVE) {
        const dragIndex = item.index;
        const hoverIndex = index;
        if (!ref.current) {
          return;
        }

        // Don't replace items with themselves
        if (dragIndex === hoverIndex) {
          return;
        }

        // Determine rectangle on screen
        const hoverBoundingRect = ref.current?.getBoundingClientRect();
        // Get vertical middle
        const hoverMiddleY =
          (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

        // Determine mouse position
        const clientOffset = monitor.getClientOffset();

        // Get pixels to the top
        const hoverClientY =
          (clientOffset as XYCoord).y - hoverBoundingRect.top;

        // Only perform the move when the mouse has crossed half of the items height
        // When dragging downwards, only move when the cursor is below 50%
        // When dragging upwards, only move when the cursor is above 50%

        // Dragging downwards
        if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
          return;
        }

        // Dragging upwards
        if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
          return;
        }

        // Time to actually perform the action
        dispatch(startMoveRow(item.dragItem, row));

        // Note: we're mutating the monitor item here!
        // Generally it's better to avoid mutations,
        // but it's good here for the sake of performance
        // to avoid expensive index searches.
        item.index = hoverIndex;
      }
    },
  });

  //custome row css - start
  const rowStyleList = row.cssStyles;

  const rowStyleObj: { [key: string]: any } = {};

  for (let index = 0; index < rowStyleList.length; index++) {
    rowStyleObj[rowStyleList[index].retrivedStyle] = rowStyleList[index].value;
  }
  //custome row css - end

  const renderSectionList = () => {
    let sectionListBySelectedRow = sectionState.filter(
      (section) => section.rowId === row.id
    );
    return sectionListBySelectedRow.map((section, i) => {
      return (
        <SectionElement section={section} index={i} isPreview={isPreview} isTab={isTab} />
      );
    });
  };

  const opacity = isDragging ? 0.2 : 1;
  const rowHighlighted = row.rowHighlight ? "row-visual " : "";
  const backGroundSelected =
    row.id === sessionState.selectedRowId &&
    sessionState.selectedType === SystemTypes.ROW
      ? "row-selected"
      : "";
  let className = "row ";
  if (isPreview) {
    className = className;
  } else {
    className = className.concat(rowHighlighted).concat(backGroundSelected);
    drop(drag(ref));
  }

  return (
    <>
      {isPreview && (
        <div
          className={`${className} ${
            row.verticalAlign === undefined ? "" : row.verticalAlign
          } ${row.horizontalAlign === undefined ? "" : row.horizontalAlign}`}
          style={{
            opacity: opacity,
            height:
              row.height === undefined ||
              row.height === "0" ||
              row.height === ""
                ? ""
                : row.height + "px",
            width:
              row.width === undefined || row.width === "0" || row.width === ""
                ? ""
                : row.width + "px",
            ...rowStyleObj,
          }}
        >
          {renderSectionList()}
        </div>
      )}
      {!isPreview && (
        <div ref={ref} key={`${index}-${row.id}`}>
          <div
            className={`${className} m-0 ${
              row.verticalAlign === undefined ? "" : row.verticalAlign
            } ${row.horizontalAlign === undefined ? "" : row.horizontalAlign}`}
            style={{
              opacity: opacity,
              height:
                row.height === undefined ||
                row.height === "0" ||
                row.height === ""
                  ? ""
                  : row.height + "px",
              width:
                row.width === undefined || row.width === "0" || row.width === ""
                  ? ""
                  : row.width + "px",
              ...rowStyleObj,
            }}
            onClick={(e) => {
              selectRowAction(e);
            }}
          >
            {renderSectionList()}
          </div>
          {!isPreview &&
            sessionState.selectedType === SystemTypes.ROW &&
            sessionState.selectedRowId === row.id && (
              <div key={index} className="action-list-panel">
                <ActionListComponent type={SystemTypes.ROW} row={row} />
              </div>
            )}
        </div>
      )}
    </>
  );
};

export default RowElement;
